<template>
  <!-- Иконка цели в кольце прогресса: дуга показывает, насколько корзина близка
       к порогу. Общая для всех карточек ленты выгод, поэтому цвет приходит пропом. -->
  <span class="goal-ring" :style="{ '--goal-ring-tone': tone }">
    <svg class="goal-ring__svg" viewBox="0 0 48 48" aria-hidden="true">
      <circle class="goal-ring__track" cx="24" cy="24" :r="RADIUS" />
      <circle
        class="goal-ring__value"
        cx="24"
        cy="24"
        :r="RADIUS"
        :stroke-dasharray="CIRCUMFERENCE"
        :stroke-dashoffset="dashOffset"
      />
    </svg>

    <span class="goal-ring__badge">
      <slot />
    </span>
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  /** Прогресс до цели, 0–100. Значения за пределами диапазона зажимаются. */
  percent: number,
  /** Тон карточки: каналы RGB из BENEFIT_TONES, например `245 158 11`. */
  tone: string
}>()

// Радиус меньше половины бокса на толщину штриха: дуга рисуется по центру линии
// и полным радиусом обрезалась бы краем viewBox.
const RADIUS = 21
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

const clampedPercent = computed(() => {
  const value = Number(props.percent)
  if (!Number.isFinite(value)) return 0
  return Math.min(100, Math.max(0, value))
})

// Дугу «прячем», сдвигая пунктир: 0 % — сдвиг во всю длину, 100 % — нулевой.
const dashOffset = computed(() => CIRCUMFERENCE * (1 - clampedPercent.value / 100))
</script>

<style scoped lang="scss">
.goal-ring {
  @apply relative grid flex-shrink-0 place-items-center w-12 h-12;

  &__svg {
    position: absolute;
    inset: 0;
    width: 3rem;
    height: 3rem;
    // Поворот на четверть: без него дуга стартует справа, а не сверху.
    transform: rotate(-90deg);
  }

  &__track {
    fill: none;
    stroke: rgb(0 0 0 / 0.08);
    stroke-width: 3.5;
  }

  &__value {
    fill: none;
    stroke: rgb(var(--goal-ring-tone));
    stroke-width: 3.5;
    stroke-linecap: round;
    transition: stroke-dashoffset 0.5s ease-out;
  }

  &__badge {
    @apply relative grid place-items-center rounded-full;
    @apply w-[38px] h-[38px] text-lg leading-none;
    @apply bg-white dark:bg-zinc-800;
    @apply shadow-sm transition-transform duration-300;
  }
}

.dark .goal-ring__track {
  stroke: rgb(255 255 255 / 0.12);
}

@media (prefers-reduced-motion: reduce) {
  .goal-ring__value,
  .goal-ring__badge {
    transition: none;
  }
}
</style>
